import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 touch-manipulation active:scale-[0.98]",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 rounded-md",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-md",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-md",
        ghost: "hover:bg-accent hover:text-accent-foreground rounded-md",
        link: "text-primary underline-offset-4 hover:underline",
        // NEW: Esports Pro Gaming Variants
        esports: "bg-gradient-to-r from-primary to-cyan-500 text-white font-semibold rounded-md shadow-lg shadow-primary/25 hover:shadow-neon hover:from-primary/90 hover:to-cyan-500/90 border border-primary/30",
        gaming: "bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold uppercase tracking-wider rounded-md shadow-cyan hover:shadow-lg",
        neon: "bg-transparent border-2 border-primary text-primary font-semibold rounded-md hover:bg-primary/10 hover:shadow-neon",
        gold: "bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold rounded-md shadow-gold hover:shadow-lg",
        success: "bg-gradient-to-r from-emerald-500 to-green-500 text-white font-semibold rounded-md shadow-lg hover:from-emerald-600 hover:to-green-600",
        live: "bg-gradient-to-r from-red-600 to-rose-500 text-white font-bold uppercase tracking-wider rounded-md shadow-[0_0_20px_rgba(239,68,68,0.4)] animate-pulse",
      },
      size: {
        default: "h-11 px-4 py-2 min-h-[44px]",
        sm: "h-10 rounded-md px-3 min-h-[40px]",
        lg: "h-12 rounded-md px-8 min-h-[48px]",
        icon: "h-11 w-11 min-h-[44px] min-w-[44px]",
        xs: "h-8 rounded-md px-2 text-xs min-h-[32px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
